<template>
  <!-- Activity list container -->

  <div class="w-full text-sm max-w-full">
    <!-- Activity list -->
    <div class="space-y-4">
      <!-- Placeholders for activity we know is coming but hasn't shown up from the server yet -->
      <AM3ActivityItem
        v-for="n in props.pendingCount"
        :key="`pending-${n}`"
        pending
      />

      <template v-if="props.activities && props.activities.length > 0">
        <!-- Loop of activities -->

        <AM3ActivityItem
          v-for="activity in props.activities"
          :key="activity.oid"
          :activity="activity"
          :customer="customer"
        />
      </template>
    </div>

    <!-- Loading indicator -->
    <div
      v-if="props.loading || isLoading"
      class="py-8 flex justify-center"
    >
      <AM3Loading
        color="brand"
        size="md"
      />
    </div>

    <!-- Empty state -->
    <div
      v-else-if="props.pendingCount === 0 && (!props.activities || props.activities.length === 0)"
      class="py-8 text-center text-sm text-disabled [&>div]:mx-auto [&>div]:my-24"
    >
      <AM3EmptyState
        heading="No activity found"
        :description="`${customer?.firstName || 'This contact'} has no recent activity to display.`"
      />
    </div>

    <!-- End of list -->
    <div
      v-if="!hasMore && !isLoading
        && (props.activities && props.activities.length > 0)"
      class="py-4 text-center text-sm text-disabled"
    >
      <!-- End of activity history -->
    </div>

    <!-- Show More Button -->
    <div
      v-if="props.showMore
        && (props.activities && props.activities.length > 0)"
      class="mt-4 text-center pb-8"
    >
      <AM3Button
        type="link"
        :text="props.showMoreText"
        @click="emit('showMoreClick')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useStore } from 'vuex'
import AM3ActivityItem from './AM3ActivityItem.vue'
import { useInfiniteScrollComposable } from '@/composables/useInfiniteScroll'

import AM3EmptyState from '@/components/am3/empty-state/AM3EmptyState.vue'
import AM3Loading from '@/components/am3-generic/base/AM3Loading.vue'
import AM3Button from '@/components/am3-generic/base/AM3Button.vue'

import type { Activity } from '@/types/resources/activity.ts'

import type { RootState } from '@/store/modules/types'

const props = defineProps({
  title: {
    type: String,
    default: 'Activity',
  },
  activities: {
    type: Array as () => Activity[],
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  showMore: {
    type: Boolean,
    default: false,
  },
  showMoreText: {
    type: String,
    default: 'See more',
  },
  pendingCount: {
    type: Number,
    default: 0,
  },
})

const hasMore = ref(false)
const isLoading = ref(false)

const store = useStore<RootState>()

const customer = computed(() => store.state.customerModule.customer)

const emit = defineEmits(['showMoreClick'])

// Load more activities
const loadMore = async () => {
  if (!hasMore.value || isLoading.value) return
  isLoading.value = true
  hasMore.value = false
  isLoading.value = false
}

// Set up infinite scroll
useInfiniteScrollComposable(loadMore, {
  distance: 200,
  debounce: 300,
})
</script>
